// Ported from the portal's `utils/discoveryCells.tsx`.
// Spec: viq-vault/.../specs/2026-06-10-discovery-admin-answer-viewing-design.md §5
import type { DiscoveryQuestion } from './types';

const EM_DASH = '—';
const RETIRED = '(retired option)';

export interface CellAnswer {
  selectedOptionIds: string[];
  freeText: string | null;
}

/** Look up an option's text by id; returns "(retired option)" when the id is no longer present. */
export function optionLabelById(question: DiscoveryQuestion, id: string): string {
  return question.options.find((o) => o.id === id)?.text ?? RETIRED;
}

/**
 * Render one answer cell to a display string (not JSX — the component layer
 * wraps it in <Typography.Text/> etc.).
 *
 * Rules (per spec §5):
 *   - no answer / empty / whitespace-only → em-dash
 *   - free_text question → freeText verbatim
 *   - choice with chips only → labels joined with ", "
 *   - choice with chips + Other → "label1, label2, Other: <freeText>"
 *   - choice with Other only → "Other: <freeText>"
 *   - any selectedOptionId not in current options → "(retired option)"
 */
export function renderAnswerCell(
  answer: CellAnswer | undefined,
  question: DiscoveryQuestion,
): string {
  const trimmedFreeText = answer?.freeText?.trim() ?? '';
  const hasFreeText = trimmedFreeText !== '';
  const hasChips = (answer?.selectedOptionIds.length ?? 0) > 0;

  if (!answer || (!hasChips && !hasFreeText)) return EM_DASH;

  if (question.type === 'free_text') {
    return trimmedFreeText || EM_DASH;
  }

  const labels = answer.selectedOptionIds.map((id) => optionLabelById(question, id));
  if (hasFreeText) labels.push(`Other: ${trimmedFreeText}`);
  return labels.join(', ');
}
